// Заведение кандидата руками. Панель, а не модалка: карточка кандидата заводится обратимо —
// её можно склеить с найденной или отправить в «не согласован» (правило 4).
//
// Живая проверка дубля идёт ПО РАСФОКУСУ телефона, а не на каждой букве: номер набирается
// одиннадцатью нажатиями, и одиннадцать запросов подряд дали бы одиннадцать мигающих
// предупреждений, из которых первые десять — про недонабранный номер.
//
// Дубль ПРЕДУПРЕЖДАЕТ, а не запрещает: телефон оставляют не все, полные тёзки встречаются, и
// запрет по совпадению заблокировал бы заведение настоящего второго человека. Кнопка
// сохранения остаётся на месте, решение принимает человек.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { useNavigate } from 'react-router';

import * as Button from '@/shared/ui/button';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { TextField } from '@/shared/ui/text-field';

import type { CandidateIn } from '../api/candidates';
import { DuplicateNotice } from '../components/DuplicateNotice';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { newIdempotencyKey, useCreateCandidateMutation, useDuplicateSearchQuery } from '../queries';
import { hiringHref } from '../route';

const EDIT_PERMISSION = 'hiring.candidate.edit';

/** Сколько цифр должно быть в номере, чтобы за ним имело смысл ходить на сервер. */
const ПОЛНЫЙ_НОМЕР = 10;

export function NewCandidatePanel({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const allowed = useCan(EDIT_PERMISSION);
  // Ключ живёт столько же, сколько ФОРМА: двойное нажатие и повтор после обрыва обязаны
  // прийти с тем же ключом, иначе в воронке заведутся два одинаковых человека.
  const [ключ] = useState(newIdempotencyKey);
  const мутация = useCreateCandidateMutation(ключ);

  const [фамилия, setФамилия] = useState('');
  const [имя, setИмя] = useState('');
  const [отчество, setОтчество] = useState('');
  const [телефон, setТелефон] = useState('');
  const [город, setГород] = useState('');
  const [регион, setРегион] = useState('');
  const [рождение, setРождение] = useState('');
  const [удостоверение, setУдостоверение] = useState('');
  const [ошибкаИмени, setОшибкаИмени] = useState<string | undefined>(undefined);
  const [отказ, setОтказ] = useState<string | null>(null);
  // Проверка запускается расфокусом, а не набором: сюда кладётся то, что уже дозрело.
  const [проверить, setПроверить] = useState<{ phone?: string; last_name?: string } | null>(null);

  const дубли = useDuplicateSearchQuery(проверить ?? {}, проверить !== null);

  function проверитьДубли() {
    const цифры = телефон.replace(/\D/g, '');
    if (цифры.length >= ПОЛНЫЙ_НОМЕР) {
      setПроверить({ phone: телефон });
      return;
    }
    if (фамилия.trim() !== '') {
      setПроверить({ last_name: фамилия.trim() });
    }
  }

  function отправить(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setОтказ(
        'Заводить кандидатов может рекрутёр и кадры. Попросите администратора выдать право на правку карточек.',
      );
      return;
    }
    if (мутация.isPending) {
      setОтказ('Карточка уже создаётся, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    if (фамилия.trim() === '' && телефон.trim() === '') {
      setОшибкаИмени(
        'Нужна хотя бы фамилия или телефон: без того и другого карточку невозможно ни найти, ни склеить с откликом.',
      );
      return;
    }
    setОшибкаИмени(undefined);
    setОтказ(null);

    const тело: CandidateIn = {
      last_name: пусто(фамилия),
      first_name: пусто(имя),
      middle_name: пусто(отчество),
      phone: пусто(телефон),
      city: пусто(город),
      region: пусто(регион),
      birth_date: рождение === '' ? null : рождение,
      guard_license_no: пусто(удостоверение),
    };

    мутация.mutate(тело, {
      onSuccess: (кандидат) => {
        onClose();
        // Открываем заведённую карточку: человек только что её создал и первым делом хочет
        // увидеть, что получилось, а не искать её в списке из семидесяти строк.
        navigate(hiringHref('candidates', { filters: {}, panels: [] }, кандидат.id));
      },
    });
  }

  return (
    <form onSubmit={отправить} noValidate className='flex flex-col gap-3'>
      {!allowed && (
        <NoPermissionNote что='заводить кандидатов' кто='Карточки ведут рекрутёр и кадры.' />
      )}

      <TextField
        id='new-candidate-last-name'
        label='Фамилия'
        value={фамилия}
        onChange={setФамилия}
        onBlur={проверитьДубли}
        error={ошибкаИмени}
        hint={
          ошибкаИмени === undefined
            ? 'Площадка отдаёт имя не всегда: если его нет, оставьте пусто и заполните после разговора.'
            : undefined
        }
      />
      <TextField id='new-candidate-first-name' label='Имя' value={имя} onChange={setИмя} />
      <TextField
        id='new-candidate-middle-name'
        label='Отчество'
        value={отчество}
        onChange={setОтчество}
      />

      <TextField
        id='new-candidate-phone'
        label='Телефон'
        value={телефон}
        onChange={setТелефон}
        onBlur={проверитьДубли}
        inputMode='tel'
        placeholder='+7 900 123-45-67'
        hint='В любом виде: приводит к общему одна функция на весь проект, на сервере.'
      />

      {дубли.data !== undefined && (
        <DuplicateNotice
          report={дубли.data}
          note='Это предупреждение, а не запрет: тёзки встречаются. Откройте найденную карточку и решите сами — если человек тот же, отклик лучше прикрепить к ней.'
        />
      )}

      <TextField id='new-candidate-city' label='Город' value={город} onChange={setГород} />
      <TextField id='new-candidate-region' label='Регион' value={регион} onChange={setРегион} />
      <DateField
        id='new-candidate-birth'
        label='Дата рождения'
        value={рождение}
        onChange={setРождение}
        hint='Нужна сверке по чёрному списку: по одной фамилии совпадений слишком много.'
      />
      <TextField
        id='new-candidate-license'
        label='Удостоверение охранника'
        value={удостоверение}
        onChange={setУдостоверение}
        hint='Номер удостоверения — самый сильный признак при сверке по чёрному списку.'
      />

      {мутация.isError && <ErrorAlert error={мутация.error} />}
      {отказ !== null && <GateNote>{отказ}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {мутация.isPending ? 'Создаём…' : 'Завести кандидата'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}

function пусто(значение: string): string | null {
  const обрезано = значение.trim();
  return обрезано === '' ? null : обрезано;
}
